import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Container, Icon, Loader } from 'semantic-ui-react';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import Image from '@plone/volto/components/theme/Image/Image';
import config from '@plone/volto/registry';

import { getActiveLanguage } from '../../helpers/homepageUrl';
import {
  filterQuadreMedicList,
  findMetgeBySlug,
  getMetgeSeoMetadata,
  getQuadreMedicBasePath,
  getQuadreMedicMetgePath,
  useQuadreMedicPageData,
} from '../../helpers/quadreMedic';
import QuadreMedicAutocomplete from '../QuadreMedic/QuadreMedicAutocomplete';
import messages from '../QuadreMedic/quadreMedicMessages';
import MoreInfoBar from '../MoreInfoBar/MoreInfoBar';
import quadreBackground from '../../theme/assets/fons-quadremedic.jpg';

const backgroundPreload = (
  <link rel="preload" as="image" href={quadreBackground} fetchpriority="high" />
);

const QuadreMedicPage = () => {
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const { metgeSlug } = useParams();
  const locale = useSelector((state) => state.intl.locale);
  const language = getActiveLanguage(location.pathname, locale);
  const metgeInputRef = useRef(null);

  const { loading, quadreMedicList, especialitatsList } =
    useQuadreMedicPageData(language);

  const [especSelectValue, setEspecSelectValue] = useState('');
  const [especSelected, setEspecSelected] = useState('');
  const [metgeSelectedValue, setMetgeSelectedValue] = useState('');
  const [metgeDetail, setMetgeDetail] = useState(null);

  useEffect(() => {
    if (loading) {
      return;
    }

    if (!metgeSlug) {
      setMetgeDetail(null);
      return;
    }

    const found = findMetgeBySlug(quadreMedicList, metgeSlug);
    if (found) {
      setMetgeDetail(found);
      return;
    }

    if (quadreMedicList.length > 0) {
      history.replace(getQuadreMedicBasePath(language));
    }
  }, [loading, metgeSlug, quadreMedicList, language, history]);

  const filteredMetges = useMemo(
    () =>
      filterQuadreMedicList(quadreMedicList, {
        especialitat: especSelected,
        metgeQuery: metgeSelectedValue,
      }),
    [quadreMedicList, especSelected, metgeSelectedValue],
  );

  const showMetgesSection =
    especSelected.length > 0 ||
    metgeSelectedValue.length > 0 ||
    !!metgeSlug ||
    !!metgeDetail;

  const seoMetadata = useMemo(() => {
    if (!metgeDetail) {
      return null;
    }
    return getMetgeSeoMetadata(
      metgeDetail,
      language,
      config.settings.publicURL || '',
    );
  }, [metgeDetail, language]);

  const handleChangeEspec = (event, value) => {
    setEspecSelectValue(value);
    setEspecSelected('');
    setMetgeSelectedValue('');
  };

  const handleSelectEspec = (value) => {
    setEspecSelected(value);
    setEspecSelectValue(value);
    setMetgeSelectedValue('');
    metgeInputRef.current?.focus();
  };

  const handleChangeMetge = (event, value) => {
    setMetgeSelectedValue(value);
  };

  const handleSelectMetge = (value) => {
    setMetgeSelectedValue(value);
  };

  const shouldRenderEspecialitat = (item, value) => {
    const isExactMatch = especialitatsList.includes(value);
    return (
      value === '' ||
      isExactMatch ||
      item.toLowerCase().indexOf(value.toLowerCase()) > -1
    );
  };

  const shouldRenderMetge = (item, value) => {
    const matchesEspecialitat = especSelected.length
      ? item.Especialitat.indexOf(especSelected) > -1
      : true;

    if (!matchesEspecialitat) {
      return false;
    }

    const scopedMetges = quadreMedicList.filter((metge) =>
      especSelected.length
        ? metge.Especialitat.indexOf(especSelected) > -1
        : true,
    );
    const metgeNames = scopedMetges.map((metge) => metge.CognomsNom);
    const isExactMatch = metgeNames.includes(value);

    return (
      isExactMatch ||
      value === '' ||
      item.CognomsNom.toLowerCase().indexOf(value.toLowerCase()) > -1
    );
  };

  const showMetgeDetail = (metge) => {
    history.push(getQuadreMedicMetgePath(language, metge.slug));
  };

  const hideMetgeDetail = () => {
    history.push(getQuadreMedicBasePath(language));
  };

  if (loading) {
    return (
      <>
        <Helmet>{backgroundPreload}</Helmet>
        <div className="quadre-medic quadre-medic-page">
          <div className="csj-page-loader">
            <Loader active inline="centered" />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        {backgroundPreload}
        {seoMetadata ? <title>{seoMetadata.title}</title> : null}
        {seoMetadata?.description ? (
          <meta name="description" content={seoMetadata.description} />
        ) : null}
        {seoMetadata ? (
          <link rel="canonical" href={seoMetadata.canonical} />
        ) : null}
      </Helmet>
      <div className="quadre-medic quadre-medic-page">
        <Container>
          <div className="quadre-medic-top">
            <h3 className="csj-heading3">
              {intl.formatMessage(messages.heading)}
            </h3>
            {!metgeDetail && (
              <div className="quadre-medic-filters">
                <p className="filters-text">
                  {intl.formatMessage(messages.filtersHelp)}
                </p>

                <div className="filter-group">
                  <label htmlFor="quadre-medic-especialitat">
                    {intl.formatMessage(messages.searchSpecialtyLabel)}
                  </label>
                  <div className="autocomplete">
                    <QuadreMedicAutocomplete
                      inputId="quadre-medic-especialitat"
                      items={especialitatsList}
                      getItemValue={(item) => item}
                      value={especSelectValue}
                      onChange={handleChangeEspec}
                      onSelect={handleSelectEspec}
                      shouldItemRender={shouldRenderEspecialitat}
                      renderItem={(item, isHighlighted) => (
                        <div
                          className={`autocomplete-item${
                            isHighlighted || item === especSelectValue
                              ? ' active'
                              : ''
                          }`}
                        >
                          {item}
                        </div>
                      )}
                    />
                    <Icon name="chevron down" className="autocomplete-icon" />
                  </div>
                </div>

                <div className="filter-group">
                  <label htmlFor="quadre-medic-metge">
                    {intl.formatMessage(messages.searchDoctorLabel)}
                  </label>
                  <div className="autocomplete">
                    <QuadreMedicAutocomplete
                      inputId="quadre-medic-metge"
                      inputRef={metgeInputRef}
                      items={quadreMedicList}
                      getItemValue={(item) => item.CognomsNom}
                      value={metgeSelectedValue}
                      onChange={handleChangeMetge}
                      onSelect={handleSelectMetge}
                      shouldItemRender={shouldRenderMetge}
                      renderItem={(item, isHighlighted) => (
                        <div
                          className={`autocomplete-item${
                            isHighlighted ||
                            item.CognomsNom === metgeSelectedValue
                              ? ' active'
                              : ''
                          }`}
                        >
                          {item.CognomsNom}
                        </div>
                      )}
                    />
                    <Icon name="chevron down" className="autocomplete-icon" />
                  </div>
                </div>
              </div>
            )}
          </div>

          {showMetgesSection && (
            <div
              className={`quadre-medic-bottom${
                metgeDetail ? ' showing-detail' : ''
              }`}
            >
              {metgeDetail ? (
                <>
                  <div className="metge-detail">
                    <div className="metge-detail-left">
                      <span className="metge-detail-nom">
                        {metgeDetail.CognomsNom}
                      </span>

                      {metgeDetail.Especialitat.length > 0 ? (
                        <div className="metge-detail-fila">
                          <span className="metge-detail-label">
                            {intl.formatMessage(messages.specialtyDetailLabel)}
                          </span>
                          <span className="metge-detail-value">
                            {metgeDetail.Especialitat.join(', ')}
                          </span>
                        </div>
                      ) : null}

                      {metgeDetail.formacions?.length > 0 ? (
                        <div className="metge-detail-fila">
                          <span className="metge-detail-label">
                            {intl.formatMessage(messages.trainingLabel)}
                          </span>
                          <ul className="metge-detail-list">
                            {metgeDetail.formacions.map((item) => (
                              <li key={item}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      ) : null}

                      {metgeDetail.carrec ? (
                        <div className="metge-detail-fila">
                          <span className="metge-detail-label">
                            {intl.formatMessage(messages.positionLabel)}
                          </span>
                          <span className="metge-detail-value">
                            {metgeDetail.carrec}
                          </span>
                        </div>
                      ) : null}

                      {metgeDetail.any_incorporacio ? (
                        <div className="metge-detail-fila">
                          <span className="metge-detail-label">
                            {intl.formatMessage(
                              messages.incorporationYearLabel,
                            )}
                          </span>
                          <span className="metge-detail-value">
                            {metgeDetail.any_incorporacio}
                          </span>
                        </div>
                      ) : null}
                    </div>

                    <div className="metge-detail-right">
                      <div className="metge-detail-right-content">
                        {metgeDetail.reflexio ? (
                          <>
                            <span className="metge-detail-reflexio-title">
                              {intl.formatMessage(messages.reflectionTitle)}
                            </span>
                            <p className="metge-detail-reflexio-text">
                              {'\u201C'}
                              {metgeDetail.reflexio}
                              {'\u201D'}
                            </p>
                          </>
                        ) : null}
                      </div>
                      {metgeDetail.image ? (
                        <Image
                          className="metge-detail-image"
                          src={metgeDetail.image}
                          alt={metgeDetail.CognomsNom}
                          fetchpriority="auto"
                        />
                      ) : null}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="metge-detail-back"
                    onClick={hideMetgeDetail}
                  >
                    <Icon name="chevron left" />
                    {intl.formatMessage(messages.backToList)}
                  </button>
                </>
              ) : (
                <div className="metges-grid">
                  {filteredMetges.map((item) => (
                    <div
                      className={`metge-card${
                        item.image ? '' : ' metge-card--no-image'
                      }`}
                      key={item.slug || item.CognomsNom}
                    >
                      <div className="metge-card-inner">
                        <div className="metge-card-content">
                          <span className="metge-nom">{item.CognomsNom}</span>
                          {item.Especialitat?.length > 0 ? (
                            <span className="metge-especialitat">
                              <span className="metge-especialitat-label">
                                {intl.formatMessage(
                                  messages.specialtyDetailLabel,
                                )}
                              </span>
                              <span className="metge-especialitat-value">
                                {item.Especialitat.join(', ')}
                              </span>
                            </span>
                          ) : null}
                        </div>
                        {item.image ? (
                          <Image
                            className="metge-card-image"
                            src={item.image}
                            alt={item.CognomsNom}
                            loading="lazy"
                          />
                        ) : null}
                      </div>
                      <button
                        type="button"
                        className="metge-card-button"
                        onClick={() => showMetgeDetail(item)}
                      >
                        {intl.formatMessage(messages.learnMore)}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Container>
      </div>
      <MoreInfoBar />
    </>
  );
};

QuadreMedicPage.propTypes = {
  content: PropTypes.objectOf(PropTypes.any),
};

export default QuadreMedicPage;
